import { FC, ReactNode, useEffect } from 'react';

import {
  ContentLayoutVariant,
  HasCommonContent,
} from '@silvertours/common-landingpages-view';
import { Runtime, analytics } from '@silvertours/front-entities';
import {
  AppInstallBanner,
  ErrorPage,
  MarketingSlider,
  PageLegacy,
  PageNavigationLegacy,
  SearchFormLegacy,
  Suppliers,
  useShowAppBanner,
} from '@silvertours/front-features';
import type { SearchData } from '@silvertours/common-landingpages-view';
import { ContentModulesRenderer } from './content';
import { ContentModules } from './ContentModules';
import { resolveVariantConfig } from './Body.variant';

type BodyProps = HasCommonContent & {
  variant: ContentLayoutVariant;
  bodyTop?: ReactNode;
  searchData?: SearchData;
  title?: string;
  content: ContentModulesRenderer;
  introductionContent?: ReactNode;
  author?: ReactNode;
  statusCode?: number;
};

const FLUGHAFEN_PALMA_STAGE_SEARCH_DATA: SearchData = {
  location: {
    displayName: 'Flughafen Palma de Mallorca',
    cityName: 'Palma de Mallorca',
    type: 'airport',
    query: 'Flughafen Palma de Mallorca (PMI)',
    iataCode: 'PMI',
    cityCode: 'PMI',
  },
  prefilterQuery: '',
  type: 'legacy',
} as SearchData;

const Body: FC<BodyProps> = ({
  variant,
  title,
  breadcrumbs,
  content,
  suppliers,
  sliderCards,
  bodyTop,
  searchData,
  author,
  introductionContent,
  statusCode,
}) => {
  const { language } = Runtime.useLocale();
  const { isShowing, onClose } = useShowAppBanner(language);
  const { isMallorcaPage } = Runtime.usePath();

  const {
    hasStageAbove,
    includeTrustSeals,
    isMinimal,
    isPlain,
    isPromo,
    isErrorPage,
    showMarketingSlider,
    showBreadcrumbs,
  } = resolveVariantConfig(variant);

  useEffect(() => {
    if (isShowing) {
      analytics.gtm.trackAppPromoBannerViewed();
      analytics.generic.trackAppPromoBannerViewed();
    }
  }, [isShowing]);

  return (
    <SearchFormLegacy.SearchProvider
      pageData={isMallorcaPage ? FLUGHAFEN_PALMA_STAGE_SEARCH_DATA : searchData}
    >
      <PageLegacy.PageContentBase>
        {isPromo && introductionContent}
        {isErrorPage && (
          <ErrorPage.Intro statusCode={statusCode as ErrorPage.ErrorCode} />
        )}
        {bodyTop}
        {!isErrorPage && (
          <Suppliers.Suppliers
            title={title}
            suppliers={suppliers.content}
            hasStageAbove={hasStageAbove}
            includeTrustSeals={includeTrustSeals}
            isMinimal={isMinimal}
            isPlain={isPlain}
            author={author}
          />
        )}
        {showMarketingSlider && (
          <MarketingSlider.MarketingSlider
            sliderCards={sliderCards?.content?.sliderCards}
          />
        )}
        {isErrorPage && <ErrorPage.InfoCards />}
        {breadcrumbs && showBreadcrumbs && (
          <PageNavigationLegacy.Breadcrumbs breadcrumbs={breadcrumbs.links} />
        )}
        <ContentModules content={content} />
        <>
          <div id="fglayer" role="application" />
          <AppInstallBanner isShowing={isShowing} onClose={onClose} />
        </>
      </PageLegacy.PageContentBase>
    </SearchFormLegacy.SearchProvider>
  );
};

export { Body };
export type { BodyProps };
